import React from 'react';
import styles from './style.module.scss';
import { listingPageModalIds } from 'constants/test-constants';
import { StandardStatus } from '@zoocasa/go-search';
import { Namespaces } from 'types/locales';
import { useTranslation } from 'next-i18next';
import { getStatusLabelKey } from 'types/reso';

export type ListingStatusProps = {
  status: StandardStatus;
  isRental?: boolean;
  className?: string | undefined;
};

export default function ListingStatus({ status, isRental, className }: ListingStatusProps) {
  const { t } = useTranslation(Namespaces.COMMON);

  const statusLabelKey = getStatusLabelKey(status, isRental);
  const statusClass = getStatusClass(status);

  return (
    <div className={`${styles.component} ${statusClass} ${className}`}>
      <p data-testid={listingPageModalIds.listingStatus}>{t(statusLabelKey)}</p>
    </div>
  );
}

/**
 * Maps a listing status to the appropriate CSS class name for styling.
 *
 * This function determines the correct CSS module class based on the listing status
 * to apply appropriate visual styling (colors, borders, etc.) to the status badge.
 * Multiple statuses may map to the same CSS class for consistent visual grouping.
 *
 * @param status the standard status of the listing from the StandardStatus enum
 * @returns A CSS module class name string that corresponds to the status styling
 *
 * @example
 * ```tsx
 * const statusClass = getStatusClass(StandardStatus.STANDARD_STATUS_ACTIVE);
 * // Returns: styles['active'] (applies active status styling)
 *
 * const statusClass = getStatusClass(StandardStatus.STANDARD_STATUS_HOLD);
 * // Returns: styles['inactive'] (applies inactive status styling)
 * ```
 *
 * @remarks
 * - Active and Active Under Contract statuses both use 'active' styling
 * - Hold, Withdrawn, and Pending statuses all use 'inactive' styling
 * - Each status maps to one of: active, closed, expired, canceled, inactive, or other
 * - The returned class name corresponds to CSS rules in style.module.scss
 * - Falls back to 'other' styling if an unrecognized status is provided
 */
function getStatusClass(status: StandardStatus) {
  let statusClass: string;
  switch (status) {
    case StandardStatus.STANDARD_STATUS_ACTIVE:
    case StandardStatus.STANDARD_STATUS_ACTIVE_UNDER_CONTRACT:
      statusClass = styles['active'];
      break;
    case StandardStatus.STANDARD_STATUS_CLOSED:
      statusClass = styles['closed'];
      break;
    case StandardStatus.STANDARD_STATUS_EXPIRED:
      statusClass = styles['expired'];
      break;
    case StandardStatus.STANDARD_STATUS_CANCELED:
      statusClass = styles['canceled'];
      break;
    case StandardStatus.STANDARD_STATUS_HOLD:
    case StandardStatus.STANDARD_STATUS_WITHDRAWN:
    case StandardStatus.STANDARD_STATUS_PENDING:
      statusClass = styles['inactive'];
      break;
    default:
      statusClass = styles['other'];
      break;
  }
  return statusClass;
}